import {
  Cell,
  Column,
  ColumnProps,
  Flex,
  Row,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
  Text,
  Truncate,
} from '@eleveo/components-react';
import { CircleIcon } from '@eleveo/icons-react';
import React, { useMemo } from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';

import { KPI_OPTIONS_PROPS, NO_VALUE_LABEL } from './config';
import { LIST_CONTAINER_TAG } from './constants';
import { useAuth } from '../../app/AuthorizationProvider';
import { TeamsReportsResult } from '../../graphql/components';
import { DateRangeFilter } from '../../utils/filter';

import type { ReportsKpiType, Values } from '../../types';
import type { CalendarDate } from '@internationalized/date';

const SUMMARY_KEY = 'summary';

export type TeamStatisticsTableProps = {
  dateRange: DateRangeFilter;
  children: React.ReactNode;
  teamsReports: TeamsReportsResult;
  visibleKpi: Values<typeof ReportsKpiType>[];
};

type ExtendedColumnProps = ColumnProps<any> & { key: React.Key };

export const TeamStatisticsTable = ({ dateRange, children, teamsReports, visibleKpi }: TeamStatisticsTableProps) => {
  const { locale, timezone } = useAuth();
  const { t } = useTranslation();

  const formatter = useDateFormatter({ dateStyle: 'medium', timeZone: timezone });

  const daysInRange = useMemo(() => {
    const days = [] as CalendarDate[];
    let currentDate = dateRange.start as CalendarDate;

    while (dateRange.end.compare(currentDate) >= 0) {
      days.push(currentDate);

      currentDate = currentDate.add({ days: 1 });
    }

    return days;
  }, [dateRange]);

  const columns: ExtendedColumnProps[] = [
    { key: 'team', children: t('team.team'), isSticky: true, minWidth: 210 },
    ...daysInRange.map(
      (day) =>
        ({
          align: 'center',
          key: `day_${day.toString()}`,
          title: <Text>{formatter.format(day.toDate(timezone))}</Text>,
          children: visibleKpi.map((kpi) => (
            <Column key={`day_${day.toString()}_${kpi}`} align="end">
              <Flex className="w-full" justifyContent="center">
                <CircleIcon
                  aria-label={t('reports.kpiForDate', {
                    kpi: t(`scheduleAdherence.${kpi}`),
                    date: formatter.format(day.toDate(timezone)),
                  })}
                  color={KPI_OPTIONS_PROPS[kpi].color}
                  fontSize={14}
                />
              </Flex>
            </Column>
          )),
        } as ExtendedColumnProps)
    ),
    ...visibleKpi.map(
      (kpi) =>
        ({
          kpi,
          align: 'end',
          isSticky: true,
          key: `total_${kpi}`,
          children: (
            <Flex alignItems="center" style={{ gap: '0.5ch' }}>
              <CircleIcon aria-label={t(`reports.total.${kpi}`)} color={KPI_OPTIONS_PROPS[kpi].color} fontSize={14} />{' '}
              {t('reports.total.total')}
            </Flex>
          ),
        } as ExtendedColumnProps)
    ),
  ];

  const transformedTableItems = useMemo(() => {
    const data = [
      ...teamsReports.result.teamStatistics,
      {
        dailyStatistics: teamsReports.result.dailyStatistics,
        statistics: teamsReports.result.statistics,
        team: {
          id: SUMMARY_KEY,
          name: t('reports.summary'),
        },
      },
    ];

    return data.map((item, index) => {
      const isSummary = index === data.length - 1; // Summary is the very last row

      const transformed: Record<string, React.ReactNode> = {
        team: (
          <Flex className="w-full font-bold" style={{ columnGap: '0.5ch' }} wrap>
            <Truncate key={item.team.id}>{item.team.name}</Truncate>
          </Flex>
        ),
        teamId: item.team.id,
      };

      item.dailyStatistics.forEach(({ date, statistics }) =>
        visibleKpi.forEach((kpi) => {
          const isValueNaN = typeof statistics[kpi] === 'string' && Number.isNaN(Number.parseFloat(statistics[kpi]));

          transformed[`day_${date}_${kpi}`] = (
            <Text {...(isSummary && { weight: 'bold' })}>
              {isValueNaN
                ? NO_VALUE_LABEL
                : statistics[kpi].toLocaleString(locale, {
                    maximumFractionDigits: 2,
                    style: 'percent',
                    minimumFractionDigits: 2,
                  })}
            </Text>
          );
        })
      );

      visibleKpi.forEach((kpi) => {
        const isValueNaN =
          typeof item.statistics[kpi] === 'string' && Number.isNaN(Number.parseFloat(item.statistics[kpi]));

        transformed[`total_${kpi}`] = (
          <Text weight="bold">
            {isValueNaN
              ? NO_VALUE_LABEL
              : item.statistics[kpi].toLocaleString(locale, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  style: 'percent',
                })}
          </Text>
        );
      });

      return transformed;
    });
  }, [teamsReports.result, locale, t, visibleKpi]);

  return (
    <Table
      className="reports__table"
      aria-label={t('reports.adherenceReportsGroupedByTeams')}
      isStickyHeader
      isStickyLastRow
      testId={LIST_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>{(column) => <Column {...column} key={column.key} />}</TableHeader>

      <TableBody items={transformedTableItems}>
        {(item) => (
          <Row key={item.teamId as string}>
            {(columnKey) => <Cell key={`${item.teamId}_${columnKey}`}>{item[columnKey]}</Cell>}
          </Row>
        )}
      </TableBody>

      <TableFooter>{children}</TableFooter>
    </Table>
  );
};
